import { Box, Button, Center, Container, Grid, Loader, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import AppHeaderUserActions from "@/components/shared/AppHeaderUserActions";
import TitleComponent from "@/components/shared/TitleComponent";
import { getSectorBySlug } from "@/pages/home/sectorCatalog";
import { useSectorEcosystem } from "./hooks/useSectorEcosystem";
import { getSectorVisual } from "./sectorEcosystem.const";
import SectorEcosystemHero from "./components/SectorEcosystemHero";
import SectorOpportunitiesCard from "./components/SectorOpportunitiesCard";
import SectorCompaniesDonutCard from "./components/SectorCompaniesDonutCard";
import SectorCollaborationModelsCard from "./components/SectorCollaborationModelsCard";
import SectorExpertsFacilitatorsCard from "./components/SectorExpertsFacilitatorsCard";
import SectorSuccessStoriesCard from "./components/SectorSuccessStoriesCard";
import SectorEcosystemHeadsCarousel from "./components/SectorEcosystemHeadsCarousel";
import { sectorListFooterLink } from "./sectorCompaniesList.const";

export default function SectorEcosystemPage() {
  const { slug } = useParams<{ slug: string }>();
  const { isAuthenticated } = useAuth();
  const sector = getSectorBySlug(slug);

  if (!slug || !sector || sector.comingSoon) {
    return <Navigate to={isAuthenticated ? "/my-discovery" : "/"} replace />;
  }

  const { data, loading, error, reload } = useSectorEcosystem(slug);
  const visual = getSectorVisual(slug);
  const backHref = isAuthenticated ? "/my-discovery" : "/";
  const opportunitiesFooter = sectorListFooterLink(slug, "opportunities", isAuthenticated);

  return (
    <Box
      pb={isAuthenticated ? "md" : 48}
      style={{
        backgroundColor: isAuthenticated ? "#f7f9fe" : "#F8FAFC",
        minHeight: isAuthenticated ? undefined : "100vh",
      }}
    >
      {isAuthenticated ? <TitleComponent rightSection={<AppHeaderUserActions />} /> : null}

      <Container
        size={1414}
        py={{ base: "md", sm: isAuthenticated ? undefined : "xl" }}
        pt={isAuthenticated ? { base: "xs", sm: "xs" } : undefined}
        pb={isAuthenticated ? { base: "md", sm: "md" } : undefined}
      >
        <Stack gap="lg">
          <Button
            component={Link}
            to={backHref}
            variant="filled"
            color="primary"
            size="xs"
            w="fit-content"
            leftSection={<IconArrowLeft size={14} />}
            styles={{
              root: {
                backgroundColor: "#04305c",
                "&:hover": { backgroundColor: "#042f5c" },
              },
            }}
          >
            Back to Discover
          </Button>

          {loading ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : error || !data ? (
            <Center py="xl">
              <Stack align="center" gap="sm">
                <Text c="dimmed">{error ?? "Sector ecosystem not found."}</Text>
                <Button variant="light" onClick={() => void reload()}>
                  Retry
                </Button>
              </Stack>
            </Center>
          ) : (
            <>
              <SectorEcosystemHero slug={slug} overview={data} visual={visual} />

              <Grid gutter="md">
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <SectorOpportunitiesCard overview={data} {...opportunitiesFooter} />
                </Grid.Col>
                <Grid.Col span={{ base: 12, lg: 6 }}>
                  <SectorEcosystemHeadsCarousel />
                </Grid.Col>
              </Grid>

              <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
                <SectorExpertsFacilitatorsCard />
                <SectorSuccessStoriesCard slug={slug} />
                <SectorCompaniesDonutCard slug={slug} />
                <SectorCollaborationModelsCard overview={data} />
              </SimpleGrid>
            </>
          )}
        </Stack>
      </Container>
    </Box>
  );
}
